<script setup lang="ts">
import { computed, useSlots } from 'vue'

export interface Props {
  disabled?: boolean
  highlight?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  highlight: false,
})

const emit = defineEmits<{
  tap: [event: PointerEvent]
  longpress: [event: PointerEvent]
}>()

var pointerdownTime: number | null = null

const disabled = computed(() => props.disabled)
const highlight = computed(() => props.highlight)

const slots = useSlots()
const isShowHighlight = computed(() => !!slots.highlight && highlight.value)
const isShowDisabled = computed(() => !isShowHighlight.value && !!slots.disabled && disabled.value)
const isShowDefault = computed(() => !isShowDisabled.value && !isShowHighlight.value)

function handlePointerdown(event: PointerEvent) {
  pointerdownTime = Date.now()
}

function handlePointerup(event: PointerEvent) {
  const ms = pointerdownTime ? Date.now() - pointerdownTime : 0
  pointerdownTime = null
  if (ms > 500) {
    emit('longpress', event)
  } else if (ms > 0) {
    emit('tap', event)
  }
}
</script>

<template>
  <transition name="fade" mode="out-in">
    <div :class="['button', { disabled }, { highlight }]" @pointerdown="handlePointerdown" @pointerup="handlePointerup">
      <div class="wrapper-background" v-if="!!slots.background">
        <slot name="background"></slot>
      </div>
      <transition name="fade" mode="out-in">
        <div class="wrapper-default" v-if="!!slots.default" v-show="isShowDefault">
          <slot name="default"></slot>
        </div>
      </transition>
      <transition name="fade" mode="out-in">
        <div class="wrapper-disabled" v-if="!!slots.disabled" v-show="isShowDisabled">
          <slot name="disabled"></slot>
        </div>
      </transition>
      <transition name="fade" mode="out-in">
        <div class="wrapper-highlight" v-if="!!slots.highlight" v-show="isShowHighlight">
          <slot name="highlight"></slot>
        </div>
      </transition>
    </div>
  </transition>
</template>

<style scoped>
.button {
  cursor: pointer;
}

.button.disabled {
  pointer-events: none;
  cursor: auto;
}

.wrapper-default,
.wrapper-disabled,
.wrapper-highlight,
.wrapper-background {
  position: absolute;
}
</style>
